import api from '@/services/api/api';

export const useLesson = (id) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { canEditLessons } = useLessonAccess();

  const enabled = computed(() => !!toValue(id) && canEditLessons.value);

  const lesson = useQuery(
    ['lesson', id],
    async () => {
      const { data } = await api.lessons.getLessonById(toValue(id));
      return data;
    },
    {
      enabled,
      staleTime: Infinity,
      select: ({ data }) => data,
      refetchOnMount: true,
    },
  );

  const lessonData = computed(() => lesson.data?.value);

  /**
   * PATCH заменяет состав урока целиком и требует title и materials вместе,
   * поэтому любая правка собирает полный набор материалов из загруженного урока.
   */
  const saveLesson = useMutation(
    async ({ title, materials }) => {
      await api.lessons.updateLesson(toValue(id), {
        title: title ?? lessonData.value?.title,
        materials: dedupeLessonMaterials(materials),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lesson', toValue(id)]);
        queryClient.invalidateQueries(['lessons']);
      },
      onError: (error) => {
        toast.error(getLessonErrorMessage(error, 'Не удалось сохранить урок'));
      },
    },
  );

  // API не принимает пустой materials, поэтому последний материал не отдаём удалить
  const submit = (payload, options) => {
    if (!payload.materials.length) {
      toast.error('В уроке должен остаться хотя бы один материал');
      return;
    }

    saveLesson.mutate(payload, options);
  };

  const updateTitle = (title) => {
    submit(
      { title, materials: getLessonMaterialsPayload(lessonData.value) },
      { onSuccess: () => toast.success('Название урока изменено') },
    );
  };

  /** Заменяет набор материалов урока для одного параграфа */
  const updateParagraphMaterials = (
    { subject, paragraph, materials },
    options,
  ) => {
    const rest = getLessonMaterialsPayload(lessonData.value).filter(
      ({ highlight_id: highlightId }) => highlightId !== paragraph.id,
    );

    const added = materials.map((material) =>
      toLessonMaterialPayload(material, subject, paragraph),
    );

    submit({ materials: [...rest, ...added] }, options);
  };

  const deleteParagraphs = (paragraphIds, options) => {
    const materials = getLessonMaterialsPayload(lessonData.value).filter(
      ({ highlight_id: highlightId }) => !paragraphIds.includes(highlightId),
    );

    submit({ materials }, options);
  };

  return {
    lesson,
    saveLesson,
    updateTitle,
    updateParagraphMaterials,
    deleteParagraphs,
  };
};
